import { AccountVoiceField, clearSavedAccountVoice } from './AccountVoiceField';
import { useEffect, useState } from 'react';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api } from './api';
import type { AccountState } from './AccountJourney';
import { ErrorNotice } from './components';

export async function passkeySignIn(rememberSignIn: boolean) {
  if (!browserSupportsWebAuthn())
    throw new Error('Passkeys are not available in this browser. Use your password to sign in.');
  const start = await api<{ requestId: string; options: PublicKeyCredentialRequestOptionsJSON }>(
    '/account/passkeys/login/options',
    'POST',
    { rememberSignIn },
  );
  const response = await startAuthentication({ optionsJSON: start.options });
  return api<AccountState>('/account/passkeys/login/verify', 'POST', {
    requestId: start.requestId,
    response,
  });
}
export function passkeyError(error: unknown) {
  return error instanceof Error && ['NotAllowedError', 'AbortError'].includes(error.name)
    ? 'Passkey sign-in was cancelled or timed out. Try again or use your password.'
    : error instanceof Error
      ? error.message
      : 'The passkey request could not finish. Try again.';
}
type SavedKey = { id: string; name: string; createdAt: string; lastUsedAt: string | null; needsRegistration?: boolean };
export function PasskeySettings({
  userId,
  email,
  onBusyChange,
}: {
  email: string;
  userId: string;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [keys, setKeys] = useState<SavedKey[]>([]),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false),
    [ready, setReady] = useState(false);
  const [name, setName] = useState(''),
    [password, setPassword] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<SavedKey | null>(null);
  const draftKey = `ig-voice:${userId}:draft:passkey`;
  const [voiceId, setVoiceId] = useState(() => {
    try { const id = localStorage.getItem(draftKey) || crypto.randomUUID(); localStorage.setItem(draftKey, id); return id; }
    catch { return ''; }
  });
  async function load() {
    try {
      setKeys((await api<{ passkeys: SavedKey[] }>('/account/passkeys')).passkeys);
      setReady(true);
    } catch (e) {
      setError(passkeyError(e));
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function run(form: HTMLFormElement) {
    const fields = new FormData(form);
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    setNotice('');
    try {
      let result: { passkeys: SavedKey[] };
      if (removing)
        result = await api('/account/passkeys/remove', 'POST', {
          id: removing.id,
          currentPassword: String(fields.get('passkey-password')),
        });
      else {
        const start = await api<{
          requestId: string;
          options: PublicKeyCredentialCreationOptionsJSON;
        }>('/account/passkeys/register/options', 'POST', {
          name: String(fields.get('passkey-name')),
          currentPassword: String(fields.get('passkey-password')),
        });
        const response = await startRegistration({ optionsJSON: start.options });
        result = await api('/account/passkeys/register/verify', 'POST', {
          requestId: start.requestId,
          response,
        });
      }
      if (!removing) {
        clearSavedAccountVoice(userId, `passkey:new:${voiceId}:name`, String(fields.get('passkey-name')));
        try { const id = crypto.randomUUID(); localStorage.setItem(draftKey, id); setVoiceId(id); }
        catch { setVoiceId(''); }
      }
      setKeys(result.passkeys);
      setNotice(
        removing
          ? 'Passkey removed from Marvin. You can also remove its saved copy from your password manager.'
          : 'Passkey added. You can now use it to sign in.',
      );
      setRemoving(null);
      setAdding(false);
      setPassword('');
      setName('');
    } catch (e) {
      setError(passkeyError(e));
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  }
  return (
    <section
      className="account-form-card account-security-section"
      aria-labelledby="passkeys-title"
    >
      <h2 id="passkeys-title">Passkeys</h2>
      <p>
        Sign in with your fingerprint, face, device PIN or a passkey saved in your password manager.
        Your password remains available.
      </p>
      {keys.some(key => key.needsRegistration) && <p>Passkeys from the previous Marvin address need to be added again. Use your password to sign in, then add a new passkey here.</p>}
      {error && <ErrorNotice message={error} />}
      {notice && <p role="status">{notice}</p>}
      {!ready ? (
        <button onClick={() => void load()}>Reload passkeys</button>
      ) : (
        <>
          {keys.length === 0 && <p>No passkeys added yet.</p>}
          {keys.map((key) => (
            <div className="account-setting-row" key={key.id}>
              <div>
                <strong>{key.name}</strong>
                <p>
                  Added {new Date(key.createdAt).toLocaleDateString()}
                  {key.lastUsedAt
                    ? ` · Last used ${new Date(key.lastUsedAt).toLocaleDateString()}`
                    : ''}
                </p>
              </div>
              <button
                disabled={busy}
                onClick={() => {
                  setRemoving(key);
                  setPassword('');
                  setNotice('');
                }}
              >
                Remove {key.name}
              </button>
            </div>
          ))}
          {browserSupportsWebAuthn() ? (
            adding || removing ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void run(e.currentTarget);
                }}
              >
                <input name="username" autoComplete="username" value={email} readOnly hidden />
                {removing ? (
                  <p>
                    Remove <strong>{removing.name}</strong>? It will no longer sign in to Marvin.
                  </p>
                ) : (
                  <>
                    <label htmlFor="passkey-name">Passkey name</label>
                    <input
                      id="passkey-name"
                      name="passkey-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      maxLength={80}
                      placeholder="For example, my laptop"
                    />
                    <AccountVoiceField key={voiceId} userId={userId} destination={`passkey:new:${voiceId}:name`} inputId="passkey-name" onChange={setName} disabled={busy || !voiceId} />
                    {!voiceId && <p role="alert">Voice recovery is unavailable on this device. You can keep typing.</p>}
                  </>
                )}
                <label htmlFor="passkey-password">Current password</label>
                <input
                  id="passkey-password"
                  name="passkey-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  maxLength={256}
                />
                <p className="account-hint">
                  Confirm your password to {removing ? 'remove' : 'add'} a sign-in method.
                </p>
                <button className="button secondary" disabled={busy}>
                  {busy ? 'Please wait…' : removing ? 'Confirm removal' : 'Create passkey'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setRemoving(null);
                    setAdding(false);
                    setPassword('');
                  }}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                className="button secondary"
                disabled={busy}
                onClick={() => {
                  setAdding(true);
                  setNotice('');
                }}
              >
                Add passkey
              </button>
            )
          ) : (
            <p>
              This browser cannot set up passkeys. Open Marvin in a browser that supports passkeys.
            </p>
          )}
        </>
      )}
    </section>
  );
}
